/**
 * 新規カード追加メニュー（CampaignBoardPage 上のフローティングボタン）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-11.5
 */
import {
  BookOpen,
  Calendar,
  CalendarClock,
  Calculator,
  ClipboardList,
  LayoutTemplate,
  Link as LinkIcon,
  Megaphone,
  MessagesSquare,
  Plus,
  Send,
  StickyNote,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { CampaignElementType } from '@goenhub/shared';

import { Button } from '@/components/ui/button';

interface Props {
  onAdd: (type: CampaignElementType) => void;
}

/**
 * Phase 23a-ext-3: カード 10 → 6 種に整理。
 * 削除: mail_draft（mail_schedule に統合）/ delay（線の after_delay に統合）/
 *       condition（線の condition に統合）/ metric（ダッシュボードで代替）
 * 残す: line_news / mail_schedule / form / mail_contacts_group / event / note
 *
 * 既存データに上記 4 種が残っていても表示は維持する（CampaignNode 側で対応）。
 * 新規追加メニューにのみ出さない。
 */
const OPTIONS: Array<{
  type: CampaignElementType;
  label: string;
  desc: string;
  icon: typeof Plus;
}> = [
  {
    type: 'line_news',
    label: 'LINE ニュース',
    desc: 'LINE 公式から一斉配信',
    icon: Megaphone,
  },
  {
    type: 'mail_schedule',
    label: '配信予約への追加',
    desc: '既存の配信予約に audience を追加（締切型・一斉配信）',
    icon: CalendarClock,
  },
  {
    type: 'mail_send',
    label: 'メール配信（個別送信）',
    desc: 'audience に個別タイミングで動的にメール送信（ナーチャリング型）',
    icon: Send,
  },
  {
    type: 'form',
    label: 'フォーム',
    desc: 'URL を共有して回答を集める',
    icon: ClipboardList,
  },
  {
    type: 'landing',
    label: 'ランディング',
    desc: '公開ランディングページに誘導（CTA リンク先として）',
    icon: LayoutTemplate,
  },
  {
    type: 'referral',
    label: 'referral（タグ付き URL）',
    desc: 'SNS 広告 / QR / 紙媒体等、外部チャネルからの流入を計測',
    icon: LinkIcon,
  },
  {
    type: 'document_request',
    label: '資料請求',
    desc: '公開資料 (/docs/:slug) への CTA リンク。campaignId 付き計測対応',
    icon: BookOpen,
  },
  {
    type: 'briefing_request',
    label: '個別説明申込',
    desc: '個別説明フォームへの CTA リンク（dealStageHint=briefing）',
    icon: MessagesSquare,
  },
  {
    type: 'quote_request',
    label: '見積依頼',
    desc: '見積依頼フォームへの CTA リンク（dealStageHint=quote）',
    icon: Calculator,
  },
  {
    type: 'mail_contacts_group',
    label: '配信先グループ',
    desc: 'グループに対して定期配信（起点）',
    icon: Users,
  },
  {
    type: 'event',
    label: 'イベント',
    desc: 'この日時に達したらフローを進める',
    icon: Calendar,
  },
  {
    type: 'note',
    label: 'メモ',
    desc: '他のスタッフへの説明書き',
    icon: StickyNote,
  },
];

export function AddCardMenu({ onAdd }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button
        type="button"
        variant="primary"
        onClick={() => setOpen(!open)}
        className="shadow-lg"
      >
        <Plus />
        カード追加
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 z-30 rounded border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg overflow-hidden min-w-[240px] max-h-[60vh] overflow-y-auto">
          {OPTIONS.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.type}
                type="button"
                onClick={() => {
                  onAdd(opt.type);
                  setOpen(false);
                }}
                className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-[var(--bg-hover)] transition-colors"
              >
                <Icon className="size-4 mt-0.5 text-[var(--text-muted)]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium">{opt.label}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">
                    {opt.desc}
                  </div>
                </div>
                <code className="text-[9px] font-mono text-[var(--text-muted)] mt-1">
                  {opt.type}
                </code>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
